import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { ReactElement, useCallback, useMemo } from 'react';
import { isFileExportAllowed } from 'services/fileService';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';

import { ButtonText } from 'components/general';
import { DocumentationBrowserDialog } from 'components/documentation/DocumentationBrowser';
import { useExportToDocumentation } from './ExportToDocumentationContext';
import { useExportToDocumentationBrowser } from './useExportToDocumentationBrowser';
import { useIssueRouteParams } from 'hooks/navigation/useRouteParams';
import { openDirectoryForm } from 'presentation/directory/openDirectoryInput';
import { isErrorWithMessage } from 'shared/utils/errorHandling';
import { useDocumentationStore } from 'store/documentation';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import {
  showErrorToastAndLog,
  showToast,
} from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { useIssueDocumentationOperations, useIssueStore } from 'store/issue';
import { useAutoEnterUploadedDirectory } from './useAutoEnterUploadedDirectory';
import { useCurrentIssue } from 'components/dataProviders/withIssue';
import {
  createLinkedDocumentationItem,
  hasLinkedDocumentationReference,
} from 'shared/domain/issue/linkedDocumentation';

export function ExportToDocumentationDialog(): ReactElement {
  const { filesToExport, isOpen, closeExportDialog } =
    useExportToDocumentation();
  const browser = useExportToDocumentationBrowser(isOpen);
  const { issueId } = useIssueRouteParams();
  const resyncAllDirectories = useDocumentationStore.use.resyncAllDirectories();
  const fetchDirectoryContent = useDocumentationStore.use.fetchDirectoryContent();
  const resyncIssue = useIssueStore.use.resyncCurrentIssue();
  const { exportToDocumentation, exportPending } =
    useIssueDocumentationOperations();
  const resetLinkedDocumentationPreview =
    useIssueStore.use.resetLinkedDocumentationPreview();
  const resetLinkedDocumentationDetails =
    useIssueStore.use.resetLinkedDocumentationDetails();
  const issue = useCurrentIssue();
  const setLinkedDocumentation = useIssueStore.use.setLinkedDocumentation();

  const exportableFiles = useMemo(
    () => filesToExport.filter((f) => f._id && isFileExportAllowed(f)),
    [filesToExport]
  );
  const hasExportableFiles = exportableFiles.length > 0;

  useAutoEnterUploadedDirectory({ isOpen, browser });

  const handleExport = useCallback(async () => {
    if (!issueId || !hasExportableFiles) return;

    let linkedDocumentation = issue?.linkedDocumentation ?? [];
    let failedCount = 0;

    for (const file of exportableFiles) {
      try {
        const result = await exportToDocumentation({
          issueId,
          fileToExport: file,
          currentDirectoryId: browser.currentDirectoryId,
          selectedDirectoryId: browser.selectedDirectoryId,
        });

        const alreadyPresent = hasLinkedDocumentationReference(
          linkedDocumentation,
          result.documentationId,
          result.versionId
        );

        if (!alreadyPresent) {
          const optimisticItem = createLinkedDocumentationItem({
            documentationId: result.documentationId,
            versionId: result.versionId,
            sourceDocumentId: typeof file._id === 'string' ? file._id : undefined,
            nameCandidates: [
              result.documentationName,
              file.description,
              file.title,
              file.data?.name,
            ],
            descriptionCandidates: [file.description],
            imageRotationCandidates: [file.imageRotation],
          });

          linkedDocumentation = [...linkedDocumentation, optimisticItem];
        }

        resetLinkedDocumentationPreview(String(result.versionId));
        resetLinkedDocumentationDetails(String(result.documentationId));
      } catch (err) {
        failedCount += 1;
        const message = isErrorWithMessage(err)
          ? String(err.message)
          : undefined;
        if (message && message !== 'Something went wrong') {
          showToast({ message, type: TOASTER_TYPES.FAILURE });
        } else {
          showErrorToastAndLog('Export to documentation failed', err);
        }
      }
    }

    if (failedCount < exportableFiles.length) {
      setLinkedDocumentation(issueId, linkedDocumentation);
      documentationClient.sync();
      resyncAllDirectories();
      const targetDirectoryId =
        browser.selectedDirectoryId ??
        browser.currentDirectoryId ??
        ROOT_DIR_ID;
      await fetchDirectoryContent(targetDirectoryId, {
        suppressLoadingIndicators: true,
      });
      resyncIssue({ force: true });
    }

    closeExportDialog();
  }, [
    browser.currentDirectoryId,
    browser.selectedDirectoryId,
    fetchDirectoryContent,
    closeExportDialog,
    exportableFiles,
    exportToDocumentation,
    hasExportableFiles,
    issue,
    issue?.linkedDocumentation,
    issueId,
    resyncAllDirectories,
    resyncIssue,
    setLinkedDocumentation,
    resetLinkedDocumentationDetails,
    resetLinkedDocumentationPreview,
  ]);

  const handleNewDirectory = useCallback(() => {
    openDirectoryForm(undefined, {
      parentId: browser.currentDirectoryId || undefined,
    });
  }, [browser.currentDirectoryId]);

  if (!hasExportableFiles) return <></>;

  return (
    <DocumentationBrowserDialog
      isOpen={isOpen}
      onClose={closeExportDialog}
      titleFallbackId={CommonTranslationKeys.exportToDocumentation}
      searchDataQa='export-to-documentation-search'
      browser={browser}
      leftActions={
        <ButtonText onClick={handleNewDirectory}>
          <FormattedMessage id={DocumentationTranslationKeys.createDirectory} />
        </ButtonText>
      }
      confirmLabelId={CommonTranslationKeys.export}
      onConfirm={handleExport}
      confirmDisabled={
        exportPending ||
        browser.loading ||
        !issueId ||
        !hasExportableFiles ||
        Boolean(browser.loadError)
      }
      confirmLoading={exportPending || browser.loading}
    />
  );
}
